/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	createContext,
	type PropsWithChildren,
	useContext,
} from 'react';
import { jsx, css } from '@compiled/react';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Select, { PopupSelect } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import type { SingleValue } from '@atlaskit/react-select';
import type { TriggerProps } from '@atlaskit/popup';
import Blanket from '@atlassian/jira-common-blanket/src/view.tsx';
import EnterEscapeHandler from '@atlassian/jira-common-components-enter-escape-handler/src/index.tsx';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

import { GroupComponentWithStickyFooter } from '@atlassian/jira-select-with-footer-options/src/GroupComponentWithStickyFooter.tsx';
import type { FooterOption } from '@atlassian/jira-select-with-footer-options/src/utils.tsx';
import { transformServerRestStatusToStatus } from '@atlassian/jira-status-config-common/src/utils/transformServerRestStatusToStatus.tsx';
import { usePanelContext } from '@atlassian/entry-point-panel';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import {
	type StatusTransition,
	type StatusTransitionOption,
	type ModifierExtended,
	CREATE_STATUS_OPTION_TYPE,
	MANAGE_WORKFLOW_OPTION_TYPE,
	EDIT_STATUS_OPTION_TYPE,
	type TargetRefSetter,
} from '../../common/types.tsx';
import { WrappedStatusLozenge } from '../../common/ui/status-lozenge/index.tsx';
import {
	getFilteredOrderedTransitions,
	isOnlyGlobalTransitions,
	transformOptions,
} from '../../common/utils.tsx';
import { CommandPaletteStatusView } from './command-palette/index.tsx';
import { CreateStatusButton } from './configure-status/CreateStatusButton.tsx';
import messages from './messages.tsx';
import { LoadingSpinner } from './spinner/index.tsx';
import { StatusButton } from './status-button/index.tsx';
import { Transition } from './transition/index.tsx';
import type { Props, SelectTarget } from './types.tsx';
import { OptionComponent, useSelectOptions } from './utils.tsx';
import { CreateStatusOption } from './configure-status/CreateStatusOption.tsx';
import { EditStatusOption } from './configure-status/EditStatusOption.tsx';
import { EditStatusButton } from './configure-status/EditStatusButton.tsx';
import { ConfigureStatusPopup } from './configure-status/ConfigureStatusPopup.tsx';
import { ConfigureStatusPanel } from './configure-status/ConfigureStatusPanel.tsx';
import { ExplainWorkflowButton } from './explain-workflow-button/index.tsx';

const defaultPopperProps = {
	modifiers: [
		{
			name: 'offset',
			options: {
				offset: [0, 8],
			},
		},
		{
			name: 'preventOverflow',
			options: {
				padding: 5,
				rootBoundary: 'document',
			},
		},
	],
	// makes it work inside modal-dialog
	strategy: 'fixed',
};

const isOptionDisabled = (option: StatusTransitionOption | FooterOption) =>
	('transition' in option && option.transition.isDisabled) ?? false;

const isTransitionOption = (
	option: StatusTransitionOption | FooterOption | Pick<StatusTransitionOption, 'transition'>,
): option is StatusTransitionOption => 'transition' in option;

const FormatTransitionOptionLabel = ({ option }: { option: StatusTransitionOption }) => {
	const { forceNoLabels = false, filteredAndOrderedTransitions } = useStatusViewContext();
	const isGlobalOnly = isOnlyGlobalTransitions(filteredAndOrderedTransitions || []);
	const showLabel = !(forceNoLabels || isGlobalOnly);

	return (
		<Box xcss={transitionOptionStyles}>
			<Transition
				transition={option.transition}
				hasLabel={showLabel}
				testId="issue-field-status.ui.status-view.transition"
			/>
		</Box>
	);
};

const FormatTransitionOptionLabelForCommandPalette = ({
	option,
	transitionProps,
}: {
	option: Pick<StatusTransitionOption, 'transition'>;
	transitionProps?: {
		addTransitionArrowSpacing?: boolean;
	};
}) => {
	const { forceNoLabels = false, filteredAndOrderedTransitions } = useStatusViewContext();
	const isGlobalOnly = isOnlyGlobalTransitions(filteredAndOrderedTransitions || []);
	const showLabel = !(forceNoLabels || isGlobalOnly);

	return (
		<Transition
			transition={option.transition}
			hasLabel={showLabel}
			addTransitionArrowSpacing={transitionProps?.addTransitionArrowSpacing}
			testId="issue-field-status.ui.status-view.transition"
		/>
	);
};

const FormatTransitionOptionLabelWithCreateStatus = ({
	option,
}: {
	option: StatusTransitionOption | FooterOption;
}) => {
	const {
		value,
		transitions,
		showConfigureStatusButtons,
		projectId,
		projectKey,
		projectType,
		issueTypeId,
		onClickConfigureStatus,
		onCloseConfigureStatus,
		onStatusCreatedOrUpdated,
		configureStatusSource,
		createStatusButtonRef,
		editStatusButtonRef,
		shouldStatusConfigTriggerModal,
	} = useStatusViewContext();
	const isConfigureStatusAvailable = showConfigureStatusButtons && projectType && projectKey;

	if (
		isConfigureStatusAvailable &&
		projectKey &&
		projectType &&
		projectId &&
		issueTypeId &&
		'value' in option
	) {
		switch (option.value) {
			case CREATE_STATUS_OPTION_TYPE:
				return (
					<CreateStatusOption
						projectId={projectId}
						projectKey={projectKey}
						projectType={projectType}
						issueTypeId={issueTypeId}
						statusNames={transitions?.map((transition) => transition?.to?.name)}
						onClick={onClickConfigureStatus}
						onClose={onCloseConfigureStatus}
						onStatusCreated={onStatusCreatedOrUpdated}
						buttonRef={createStatusButtonRef}
						source={configureStatusSource}
						shouldTriggerModal={shouldStatusConfigTriggerModal}
					/>
				);
			case EDIT_STATUS_OPTION_TYPE:
				return transitions && value ? (
					<EditStatusOption
						status={transformServerRestStatusToStatus(value)}
						statuses={transitions
							.map((transition) => transformServerRestStatusToStatus(transition?.to))
							.filter(
								(status, index, self) =>
									index === self.findIndex((t) => t.statusName === status.statusName),
							)}
						projectId={projectId}
						projectKey={projectKey}
						issueTypeId={issueTypeId}
						statusNames={transitions?.map((transition) => transition?.to?.name)}
						onClick={onClickConfigureStatus}
						onClose={onCloseConfigureStatus}
						onStatusUpdated={onStatusCreatedOrUpdated}
						source={configureStatusSource}
						buttonRef={editStatusButtonRef}
						shouldTriggerModal={shouldStatusConfigTriggerModal}
					/>
				) : (
					<></>
				);
			case MANAGE_WORKFLOW_OPTION_TYPE:
				return <Box xcss={manageWorkflowStyles}>{option.label}</Box>;
			default:
				break;
		}
	}
	if (isTransitionOption(option)) {
		return <FormatTransitionOptionLabel option={option} />;
	}
};

const CommandPaletteStatusViewComponent = () => {
	const {
		isLoading,
		value,
		transitions,
		onSuccess,
		onFetch,
		onUpdateStatusMutation,
		filteredAndOrderedTransitions,
	} = useStatusViewContext();
	const wasFetchSuccessful = transitions != null;

	return (
		<CommandPaletteStatusView
			transitions={filteredAndOrderedTransitions}
			currentStatus={value}
			onSuccess={onSuccess}
			wasFetchSuccessful={wasFetchSuccessful}
			renderTransition={useCallback(
				(option, transitionProps) => (
					<FormatTransitionOptionLabelForCommandPalette
						option={option}
						transitionProps={transitionProps}
					/>
				),
				[],
			)}
			onFetch={onFetch}
			isLoading={isLoading}
			onUpdateStatusMutation={onUpdateStatusMutation}
		/>
	);
};

const Lozenge = ({
	targetRef,
	triggerProps,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
}) => {
	const {
		isEditable = true,
		isSaving = false,
		error,
		value,
		statusButtonRef,
		isOpen,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	if (isSaving) {
		return (
			<Blanket border={false}>
				<WrappedStatusLozenge
					targetRef={targetRef}
					status={value}
					error={error}
					contentRef={statusButtonRef}
					triggerProps={triggerProps}
				/>
			</Blanket>
		);
	}

	return (
		<WrappedStatusLozenge
			ariaLabel={`${value.name} - ${formatMessage(messages.changeStatus)}`}
			error={error}
			hasHoverState
			isEditable={isEditable}
			isOpen={isOpen}
			status={value}
			targetRef={targetRef}
			contentRef={statusButtonRef}
			triggerProps={triggerProps}
		/>
	);
};

const StatusButtonComponent = ({
	targetRef,
	triggerProps,
	helperTextId,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
	helperTextId?: string;
}) => {
	const {
		isEditable = true,
		isSaving = false,
		value,
		statusButtonRef,
		shouldFitContainer = true,
		isOpen,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	const statusButton = (
		<StatusButton
			ariaLabel={`${value.name} - ${formatMessage(messages.changeStatus)}`}
			isEditable={isEditable}
			isOpen={isOpen}
			status={value}
			targetRef={targetRef}
			statusButtonRef={statusButtonRef}
			shouldFitContainer={shouldFitContainer}
			triggerProps={triggerProps}
			helperTextId={helperTextId}
		/>
	);
	return isSaving ? <Blanket>{statusButton}</Blanket> : statusButton;
};

const FocusWrapper = ({ children }: PropsWithChildren) => {
	const { isSaving = false } = useStatusViewContext();
	const childWrapperRef = useRef<unknown>(null);
	const previousIsSaving = usePrevious(isSaving);

	useEffect(() => {
		if (previousIsSaving === true && isSaving === false) {
			childWrapperRef.current &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				childWrapperRef.current.children.length > 0 &&
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				childWrapperRef.current.children[0].focus();
		}
	}, [isSaving, previousIsSaving]);
	// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
	return <div ref={childWrapperRef}>{children}</div>;
};

const Target = ({
	targetRef,
	triggerProps,
	helperTextId,
}: {
	targetRef?: TargetRefSetter;
	triggerProps?: TriggerProps;
	helperTextId?: string;
}) => {
	const { value, appearance, registerInCommandPalette } = useStatusViewContext();
	if (value) {
		if (appearance === 'lozenge') {
			return (
				<FocusWrapper>
					<Lozenge targetRef={targetRef} triggerProps={triggerProps} />
				</FocusWrapper>
			);
		}
		if (appearance === 'button') {
			return (
				<>
					<FocusWrapper>
						<StatusButtonComponent
							targetRef={targetRef}
							triggerProps={triggerProps}
							{...(fg('jfp_a11y_issue_create_form_field_with_description') && { helperTextId })}
						/>
					</FocusWrapper>
					{registerInCommandPalette && <CommandPaletteStatusViewComponent />}
				</>
			);
		}
	}
	return <div />;
};

const Footer = () => {
	const {
		footer,
		showConfigureStatusButtons,
		value,
		transitions,
		onClickConfigureStatus,
		onCloseConfigureStatus,
		onStatusCreatedOrUpdated,
		onClickExplainWorkflow,
		createStatusButtonRef,
		editStatusButtonRef,
		configureStatusSource,
		shouldStatusConfigTriggerModal,
		projectKey,
		projectId,
		projectType,
		issueTypeId,
	} = useStatusViewContext();
	const { formatMessage } = useIntl();
	const showCreateStatusButton =
		showConfigureStatusButtons && projectKey && projectType && projectId;

	const showEditStatusButton = showCreateStatusButton && value && transitions;

	if (!footer && !showCreateStatusButton) {
		return null;
	}

	const footerContent = (
		<>
			{showCreateStatusButton && (
				<CreateStatusButton
					projectId={projectId}
					projectKey={projectKey}
					projectType={projectType}
					// issueTypeId is not immediately available on first render
					// temporary empty string will trigger a loading state within the create status modal
					issueTypeId={issueTypeId ?? ''}
					statusNames={transitions?.map((transition) => transition?.to?.name)}
					onClick={onClickConfigureStatus}
					onClose={onCloseConfigureStatus}
					onStatusCreated={onStatusCreatedOrUpdated}
					buttonRef={createStatusButtonRef}
					source={configureStatusSource}
					shouldTriggerModal={shouldStatusConfigTriggerModal}
				/>
			)}
			{showEditStatusButton && (
				<EditStatusButton
					status={transformServerRestStatusToStatus(value)}
					statuses={transitions
						.map((transition) => transformServerRestStatusToStatus(transition?.to))
						.filter(
							(status, index, self) =>
								index === self.findIndex((t) => t.statusName === status.statusName),
						)}
					projectId={projectId}
					projectKey={projectKey}
					// issueTypeId is not immediately available on first render
					// temporary empty string will trigger a loading state within the edit status modal
					issueTypeId={issueTypeId ?? ''}
					statusNames={transitions?.map((transition) => transition?.to?.name)}
					onClick={onClickConfigureStatus}
					onClose={onCloseConfigureStatus}
					onStatusUpdated={onStatusCreatedOrUpdated}
					source={configureStatusSource}
					buttonRef={editStatusButtonRef}
					shouldTriggerModal={shouldStatusConfigTriggerModal}
				/>
			)}
			{footer}
			{fg('jira-pandora-ai-explain-workflow') && (
				<ExplainWorkflowButton onClick={onClickExplainWorkflow} />
			)}
		</>
	);

	return (
		<Flex direction="column" xcss={statusViewFooterStyles}>
			<fieldset>
				<legend css={visuallyHiddenLegendStyles}>{formatMessage(messages.status)}</legend>
				{footerContent}
			</fieldset>
		</Flex>
	);
};

type Context = Props & {
	shouldStatusConfigTriggerModal: boolean;
	isOpen: boolean;
	setIsConfigureStatusModalOpen: (isOpen: boolean) => void;
	onClickConfigureStatus: () => void;
	onCloseConfigureStatus: () => void;
	onClickExplainWorkflow: () => void;
	createStatusButtonRef: React.RefObject<HTMLButtonElement> | null;
	editStatusButtonRef: React.RefObject<HTMLButtonElement> | null;
	filteredAndOrderedTransitions: StatusTransition[];
};

const StatusViewContext = createContext<Context>({
	isLoading: false,
	value: {
		id: '',
		name: '',
		statusCategory: { id: 0 },
	},
	transitions: [],
	appearance: 'button',
	shouldStatusConfigTriggerModal: true,
	isOpen: false,
	setIsConfigureStatusModalOpen: noop,
	onClickConfigureStatus: noop,
	onCloseConfigureStatus: noop,
	onClickExplainWorkflow: noop,
	createStatusButtonRef: null,
	editStatusButtonRef: null,
	filteredAndOrderedTransitions: [],
});

const useStatusViewContext = () => {
	const context = useContext(StatusViewContext);
	if (!context) {
		throw new Error('useStatusViewContext must be used within a StatusViewProvider');
	}
	return context;
};

export const StatusView = (props: Props) => {
	const {
		isEditable = true,
		isSaving = false,
		isLoading,
		value,
		transitions,
		isDefaultSelect = false,
		selectedTransition,
		onChange = noop,
		onOpen = noop,
		onClose = noop,
		shouldCaptureEscape = true,
		customNoOptionsMessage,
		statusButtonRef,
		popperProps,
		fieldId,
		showConfigureStatusButtons,
		configureStatusDialogType = 'modal',
		configureStatusPopupPlacement = 'bottom-end',
		projectKey,
		projectType,
		// for testing
		defaultOpen = false,
		onOpenManageWorkflow,
		autofocus = false,
		helperTextId,
	} = props;
	const [isOpen, setIsOpen] = useState(false);

	const selectRef = useRef<AtlaskitSelectRefType | null>(null);
	const wasChanged = useRef<boolean>(false);

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const wasFetchSuccessful = transitions != null;
	const filteredAndOrderedTransitions = useMemo(
		() => getFilteredOrderedTransitions(transitions, value.id),
		[transitions, value.id],
	);

	const isConfigureStatusAvailable = showConfigureStatusButtons && projectType && projectKey;

	const createStatusButtonRef = useRef<HTMLButtonElement>(null);
	const editStatusButtonRef = useRef<HTMLButtonElement>(null);

	const [isConfigureStatusModalOpen, setIsConfigureStatusModalOpen] = useState(false);
	const [isConfigureStatusPopupOpen, setIsConfigureStatusPopupOpen] = useState(false);
	const [shouldRefocus, setShouldRefocus] = useState(false);

	useEffect(() => {
		if (shouldRefocus) {
			statusButtonRef?.current?.focus();
			setShouldRefocus(false);
		}
	}, [shouldRefocus, statusButtonRef]);

	const {
		Panel: ConfigureStatusPopupContent,
		Provider: ConfigureStatusPopupTriggerContextProvider,
	} = usePanelContext(ConfigureStatusPanel);

	const shouldRenderSelectComponent = isDefaultSelect && !!selectedTransition;
	const shouldStatusConfigTriggerModal =
		configureStatusDialogType === 'modal' || shouldRenderSelectComponent;

	const onCloseConfigureStatus = useCallback(() => {
		if (shouldStatusConfigTriggerModal) {
			setIsConfigureStatusModalOpen(false);
		} else {
			setIsConfigureStatusPopupOpen(false);
		}
		setShouldRefocus(true);
	}, [shouldStatusConfigTriggerModal]);

	const onClickConfigureStatus = useCallback(() => {
		if (shouldStatusConfigTriggerModal) {
			setIsConfigureStatusModalOpen(true);
		} else {
			setIsConfigureStatusPopupOpen(true);
		}
	}, [shouldStatusConfigTriggerModal]);

	const onClickExplainWorkflow = useCallback(() => {
		statusButtonRef?.current?.click();
		setShouldRefocus(true);
	}, [statusButtonRef]);

	useEffect(() => {
		if (autofocus) {
			selectRef.current?.focus?.();
		}
	}, [autofocus]);

	// specifically for screen readers
	const getTransitionOptionLabel = (option: StatusTransitionOption | FooterOption) => {
		if (isTransitionOption(option)) {
			return option.transition.isGlobal
				? formatMessage(messages.transitionTo, { statusName: option.label })
				: `${option.transition.name} → ${option.transition.to.name}`;
		}
		return option.label;
	};

	const mergedPopperProps = useMemo(() => merge(defaultPopperProps, popperProps), [popperProps]);

	const handleChangeStatus = useCallback(
		(transition: StatusTransition) => {
			const { hasScreen } = transition;
			wasChanged.current = true;

			const analyticsEvent = createAnalyticsEvent({
				action: 'transitionSelected',
				actionSubject: 'statusField',
			});

			fireUIAnalytics(analyticsEvent, 'transitionSelectedStatusField', {
				hasScreen,
			});

			traceUFOPress('issue.fields.status.popup.submit');

			onChange?.(transition, analyticsEvent);
		},
		[onChange, createAnalyticsEvent],
	);

	const handleChange = useCallback(
		(option: SingleValue<StatusTransitionOption | FooterOption>) => {
			if (
				isConfigureStatusAvailable &&
				option?.value &&
				[CREATE_STATUS_OPTION_TYPE, MANAGE_WORKFLOW_OPTION_TYPE, EDIT_STATUS_OPTION_TYPE].includes(
					option.value,
				)
			) {
				switch (option?.value) {
					case CREATE_STATUS_OPTION_TYPE:
						createStatusButtonRef?.current?.click();
						setIsConfigureStatusModalOpen(true);
						break;
					case EDIT_STATUS_OPTION_TYPE:
						editStatusButtonRef?.current?.click();
						setIsConfigureStatusModalOpen(true);
						break;
					case MANAGE_WORKFLOW_OPTION_TYPE:
						onOpenManageWorkflow && onOpenManageWorkflow();
						break;
					default:
						break;
				}
				return;
			}

			if (option && isTransitionOption(option)) {
				handleChangeStatus(option.transition);
			}
		},
		[handleChangeStatus, isConfigureStatusAvailable, onOpenManageWorkflow],
	);

	const handleOpen = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'openDropdown',
			actionSubject: 'statusField',
		});
		fireUIAnalytics(analyticsEvent, 'openDropdownStatusField');

		onOpen?.(analyticsEvent);
		setIsOpen(true);
	}, [onOpen, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		wasChanged.current = false;
		onClose?.();
		setIsOpen(false);
		setShouldRefocus(true);
	}, [onClose]);

	const isStatusInlineFooterEnabled = useMemo(
		() => showConfigureStatusButtons,
		[showConfigureStatusButtons],
	);
	const transformedOptions = useMemo(
		() => transformOptions(filteredAndOrderedTransitions || []),
		[filteredAndOrderedTransitions],
	);

	const selectOptions = useSelectOptions(transformedOptions, isStatusInlineFooterEnabled);

	const getNoOptionsMessage = () => {
		if (customNoOptionsMessage) {
			return customNoOptionsMessage;
		}
		const noPermissionMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.noPermissionIssueTermRefresh
			: messages.noPermission;
		return formatMessage(wasFetchSuccessful ? noPermissionMessage : messages.cannotLoadTransitions);
	};

	const statusPopup = shouldRenderSelectComponent ? (
		<Select<StatusTransitionOption | FooterOption>
			label={formatMessage(messages.selectStatus)}
			inputId={fieldId}
			noOptionsMessage={getNoOptionsMessage}
			formatOptionLabel={(option) => (
				<FormatTransitionOptionLabelWithCreateStatus option={option} />
			)}
			getOptionLabel={getTransitionOptionLabel}
			options={selectOptions}
			isLoading={isLoading}
			isDisabled={!isEditable}
			isOptionDisabled={isOptionDisabled}
			onChange={handleChange}
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			value={transformOptions([selectedTransition!])[0]}
			isSearchable={false}
			onMenuOpen={handleOpen}
			closeMenuOnSelect
			components={{
				LoadingMessage: () => <LoadingSpinner />,
				...(isStatusInlineFooterEnabled && {
					Group: GroupComponentWithStickyFooter<StatusTransitionOption | FooterOption>,
					Option: OptionComponent,
				}),
			}}
			menuIsOpen={isConfigureStatusModalOpen ? false : undefined}
			popperProps={mergedPopperProps}
			ref={selectRef}
		/>
	) : (
		<PopupSelect<StatusTransitionOption, false, ModifierExtended>
			inputId={fieldId}
			noOptionsMessage={getNoOptionsMessage}
			formatOptionLabel={(option) => <FormatTransitionOptionLabel option={option} />}
			getOptionLabel={getTransitionOptionLabel}
			options={transformedOptions}
			target={({ ref }: SelectTarget) => (
				<Target
					targetRef={statusButtonRef ? mergeRefs(ref, statusButtonRef) : ref}
					{...(fg('jfp_a11y_issue_create_form_field_with_description') && { helperTextId })}
				/>
			)}
			isLoading={isLoading}
			isDisabled={!isEditable}
			isOptionDisabled={isOptionDisabled}
			searchThreshold={99999}
			onChange={handleChange}
			onOpen={handleOpen}
			handleClick={handleOpen}
			onClose={handleClose}
			closeMenuOnSelect
			components={{
				LoadingMessage: () => <LoadingSpinner />,
			}}
			isOpen={isConfigureStatusModalOpen ? false : undefined}
			popperProps={mergedPopperProps}
			footer={<Footer />}
			defaultIsOpen={defaultOpen}
			label={formatMessage(messages.selectStatus)}
			shouldCloseMenuOnTab={false}
		/>
	);

	const stableProps = useShallowStableReference(props);
	const contextValues = useMemo(
		() => ({
			...stableProps,
			shouldStatusConfigTriggerModal,
			isOpen,
			setIsConfigureStatusModalOpen,
			onClickConfigureStatus,
			onCloseConfigureStatus,
			onClickExplainWorkflow: fg('jira-pandora-ai-explain-workflow')
				? onClickExplainWorkflow
				: noop,
			createStatusButtonRef,
			editStatusButtonRef,
			filteredAndOrderedTransitions,
		}),
		[
			stableProps,
			shouldStatusConfigTriggerModal,
			isOpen,
			setIsConfigureStatusModalOpen,
			onClickConfigureStatus,
			onCloseConfigureStatus,
			onClickExplainWorkflow,
			filteredAndOrderedTransitions,
		],
	);
	/* This prevents the dropdown from being accessible while saving/not editable,
	 * because for the underlying PopupSelect, the `isDisabled` prop only prevents
	 * the actual dropdown items from being selectable - the dropdown itself still
	 * pops up/down.
	 */
	if (isSaving || !isEditable) {
		return (
			<StatusViewContext.Provider value={contextValues}>
				<Target />
			</StatusViewContext.Provider>
		);
	}

	return (
		<StatusViewContext.Provider value={contextValues}>
			{isConfigureStatusPopupOpen ? (
				<ConfigureStatusPopup
					placement={configureStatusPopupPlacement}
					renderTarget={Target}
					renderPopupContent={() => <ConfigureStatusPopupContent />}
					isTargetEditable={isEditable}
					onClose={() => setIsConfigureStatusPopupOpen(false)}
				/>
			) : (
				<ConfigureStatusPopupTriggerContextProvider>
					<ModalContextProvider>
						{!shouldCaptureEscape &&
						(fg('treegrid_keyboard_navigation') || fg('nin-grid-keyboard-navigation')) ? (
							statusPopup
						) : (
							<EnterEscapeHandler onEscape={noop}>{statusPopup}</EnterEscapeHandler>
						)}
					</ModalContextProvider>
				</ConfigureStatusPopupTriggerContextProvider>
			)}
		</StatusViewContext.Provider>
	);
};

const statusViewFooterStyles = xcss({
	borderTop: `2px solid ${token('color.border')}`,
});

const manageWorkflowStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.medium',
});

const visuallyHiddenLegendStyles = css({
	clip: 'rect(1px, 1px, 1px, 1px)',
	height: '1px',
	width: '1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
});

const transitionOptionStyles = xcss({
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});

export default StatusView;
